<template>
	<div id="domain-detail" class="container panel">
		<div class="row">
			<div class="col-md-3 sidebar">
				<side-bar></side-bar>
			</div>

			<div class="col-md-9 content">
				<dash-head :title="name" subtitle="Password Security Portal"></dash-head>		

				<hr class="mt-3 mb-3">
				<div v-if="!domainProcessed" class="alert alert-custom" role="alert">
  					<span class="icon icon-circle-with-cross mr-2"></span>This domain is still being processed
				</div>

				<div v-if="domainProcessed && !error && !audit && !domain.notificationCount" class="alert alert-custom" role="alert">
  					<span class="icon icon-circle-with-cross mr-2"></span>This domain has not been audited.
				</div>

				<div v-if="domainProcessed && !error && audit && !domain.notificationCount" class="alert alert-custom-none" role="alert">
  					<span class="icon icon-check mr-2"></span>
					<a :href="'/audit/' + audit.rowKey" @click.prevent="viewAudit">
						This domain was last audited on {{audit.date.toLocaleDateString()}}.
					</a>
				</div>

				<div v-if="domainProcessed && !error && domain.notificationCount" class="alert alert-custom-high" role="alert">
					<span class="icon icon-warning mr-2"></span>
					<a :href="'/domain/notifications/' + name" @click.prevent="viewNotifications">
						There are {{domain.notificationCount}} new breached credential notifications for this domain.
					</a>
				</div>

				<error :error="error"></error>

				<div class="row text-center mt-4">
					<div class="col-md-4 mb-4 mb-md-3">
						<div class="w-3 mx-auto">
							<chart type="doughnut" :width="168" :height="168"
								:useBlank="true"
								:values="chartDirectIndirectValues"							
								:labels="['Domain Specific', 'Shared']"
								:backgroundColors="['rgba(255, 99, 132, 0.2)', 'rgba(255, 159, 64, 0.2)']"
								:borderColors="['rgba(255, 99, 132, 1)', 'rgba(255, 159, 64, 1)']">
							</chart>
						</div>
						<strong class="text-muted">Breached Credentials</strong>
						<h4>Domain vs Shared</h4>
					</div>
					<div class="col-md-4 mb-4 mb-md-3">
						<div class="w-3 mx-auto">
							<chart type="doughnut" :width="168" :height="168"		
								:useBlank="true"				
								:values="[domain.totalBreached, domain.total - domain.totalBreached]"
								:labels="['Breached Employees', 'Remaining']"
								:backgroundColors="['rgba(255, 99, 132, 0.2)', 'rgba(54, 162, 235, 0.2)']"
								:borderColors="['rgba(255, 99, 132, 1)', 'rgba(54, 162, 235, 1)']">
							</chart>
						</div>
						<strong class="text-muted">Breached Credentials</strong>
						<h4>Breaches vs Employees</h4>
					</div>
					<div class="col-md-4 mb-4 mb-md-3">
						<div class="w-3 mx-auto">
							<chart type="doughnut" :width="168" :height="168"	
								:useBlank="true"					
								:values="chartIndustryScores"
								:labels="chartIndustryNames">
							</chart>
						</div>
						<strong class="text-muted">Breached Credentials</strong>
						<h4>Industry Comparison</h4>
					</div>
				</div>

				<div class="hr-divider mt-2 mb-3">
  					<h3 class="hr-divider-content hr-divider-heading"><!--Domain Details--></h3>
				</div>

				<div class="row mt-3">
  					<div class="col-md-12">
						<p class="font-weight-normal" v-if="domainProcessed">{{summaryText}}</p>
						<div class="text-right mt-4">
							<button class="btn btn-outline-primary px-3" @click.prevent="editDomain">Domain Detail</button>								
						</div>
					</div>
				</div>

				<div class="row mt-3" v-if="domainProcessed">
					<div class="col-md-6 mb-5">
					
						<list-group  class="mb-3" heading="Top Domain Credentials">
							<list-group-item 
								v-for="result in topBreaches" 
								:key="result.name" 
								:description="result.primaryEmail" 
								:detail="result.passwords.length" 
								:url="'/domain/email/' + result.primaryEmail">
							</list-group-item>
						</list-group>
					</div>
		
				  	<div class="col-md-6 mb-5">
						<list-group  class="mb-3" heading="Highest Overall Score">
							<list-group-item 
								v-for="result in topRisks" 
								:key="result.name" 
								:description="result.primaryEmail" 
								:detail="result.score" 
								:url="'/domain/email/' + result.primaryEmail">
							</list-group-item>
						</list-group>
     
						<div class="text-right mt-4">
							<a :href="'/domain/notifications/' + name" class="btn btn-outline-primary px-3" @click.prevent="viewNotifications">View Notifications</a>
							<a :href="'/domain/results/' + name" class="btn btn-outline-primary px-3" @click.prevent="viewBreaches">View Credentials</a>
						</div>
  					</div>
				</div>

			</div>
		</div>	
	</div>
</template>

<script>
	import SideBar from './SideBar'
	import DashHead from './DashHead'
	import ListGroup from './ListGroup'
	import ListGroupItem from './ListGroupItem'
	import Chart from './Chart'
	import EditDomainForm from './EditDomainForm'
	import Error from './Error'

	import api from '../api'
	import router from '../router'
	import store from '../state'

	export default {
		name: 'DomainDetailPanel',
		components: {SideBar, DashHead, ListGroup, ListGroupItem, Chart, EditDomainForm, Error},
		data() {
			return {
				topBreaches: null,
				topRisks: null,
				summaryText: 'Please wait while the summary data is loaded ....',
				showEditDomain: false,
				error: null
			}
		},
		methods: {
			viewBreaches(event) {
				router.push('/domain/results/' + this.name);
			},
			viewNotifications(event) {
				router.push('/domain/notifications/' + this.name);
			},
			viewAudit(event) {
				router.push('/audit/' + this.audit.rowKey);
			},
			editDomain(value) {
				router.push(`/domain/${this.domain.value}/edit`);
			}
		},
		computed: {
			audit() {
				//Loop through all the audits, looking for this domain
				let lastAudit = null;
				
				for (var audit of store.state.audits) {
					if (audit.domain === this.name) {
						if (!lastAudit || audit.date > lastAudit.date) lastAudit = audit;
					}
				}
				
				return lastAudit;
			},
			name() {
				return router.currentRoute.params.name;
			},
			domain() {
				return store.getters.getDomain(router.currentRoute.params.name);
			},
			domainProcessed() {
				return this.domain.statusDescription == 'Completed';
			},
			validDomains() {
				return store.state.domains.filter(domain => domain.value !== this.domain.value && domain.totalScore > 0);
			},
			segmentedDomains() {
				return this.validDomains.filter(domain => this.domain.industry === domain.industry)
			},
			chartDirectIndirectValues() {
				return [this.domain.totalBreached, this.domain.totalScore - this.domain.totalBreached];			
			},
			chartIndustryScores() {
				const results = this.segmentedDomains.map(domain => domain.totalScore);
				results.unshift(this.domain.totalScore);
				return results;
			},
			chartIndustryNames() {
				const industryDescription = this.domain.industryDescription;
				const results = this.segmentedDomains.map((domain, index) => domain.value); //`Other ${industryDescription}`
				results.unshift(this.domain.value);
				return results;
			}
		},		
		async mounted() {
			
			if (this.domainProcessed) {
				
				try
				{
					const response = await api.get(`domain/${this.name}/top`);	

					//We have a tuple returned from the .net api, so breaches is item1 and risks is item2
					const topBreaches = response.data.topBreaches;
					const topRisks = response.data.topRisk;

					//Copy across the source data, in case it has been updated
					store.commit('setDomainSourceTotal', {domain: this.domain, sourceTotal: response.data.sourceTotal});
					store.commit('setDomainSourceAdditions', {domain: this.domain, sourceAdditions: response.data.sourceAdditions});

					//Loop through and update emails in each array
					topBreaches.forEach(e => e.primaryEmail = `${e.name}@${this.name}`);
					topRisks.forEach(e => e.primaryEmail = `${e.name}@${this.name}`);

					topBreaches.sort((a,b) => b.passwords.length - a.passwords.length);
					topRisks.sort((a,b) => b.score - a.score);

					this.topBreaches = topBreaches;
					this.topRisks = topRisks;
				}
				catch(err) {			
					this.error = err;
				}

				//Get the summary text if the domain has been processed
				try {
					const response = await api.get(`domain/${this.name}/text`);	
					this.summaryText = response.data.text;
				}
				catch(err) {			
					this.error = err;	
				}
			}
		}
	}
</script>


// WEBPACK FOOTER //
// src/components/DomainDetailPanel.vue